import React, { useCallback, useEffect, useState } from 'react'

import {
  useGetArtboardAtPosition,
  useLockCamera,
  useMapRelativePositionToCameraPosition,
  useStatus,
  usePRFileArtboards,
} from '@sketch-hq/sketch-web-renderer'

import { DraftCommentProvider } from '@sketch/modules-common'
import { AnnotationOverlayContext } from '../../../annotations/components'
import { MouseCoordinates } from '../../../annotations/types'
import { AnnotationDotFragment } from '@sketch/gql-types'
import { useHideAnnotationDots } from '../../../annotations/hooks'

interface ArtboardAnnotationsOverlayContextProps
  extends Pick<
    React.ComponentProps<typeof AnnotationOverlayContext>,
    'children' | 'disabled' | 'isViewingLatestVersion' | 'hidden'
  > {
  permanentPageId: string
}

export const ArtboardAnnotationsOverlayContext = (
  props: ArtboardAnnotationsOverlayContextProps
) => {
  const { permanentPageId, hidden, ...otherProps } = props

  const status = useStatus()
  const lockCamera = useLockCamera()
  const [lockCameraState, setLockCameraState] = useState(false)

  useEffect(() => {
    lockCamera(lockCameraState)
  }, [lockCamera, lockCameraState])

  const getArtboardAtPosition = useGetArtboardAtPosition()
  const artboards = usePRFileArtboards()

  const mapRelativePosition = useMapRelativePositionToCameraPosition()

  const getElementAtPosition = useCallback(
    async (mouseCoordinates: MouseCoordinates) => {
      const artboard = await getArtboardAtPosition(
        mouseCoordinates.clientX,
        mouseCoordinates.clientY
      )

      if (artboard) {
        return {
          type: 'ARTBOARD' as const,
          permanentId: artboard.getLayerUUID(),
          bounds: artboard.getAbsoluteExtent(),
          permanentPageId,
        }
      }

      return null
    },
    [getArtboardAtPosition, permanentPageId]
  )

  const getPositionRelativeToOrigin = useCallback(
    (mouseCoordinates: MouseCoordinates) => {
      if (status.type !== 'READY') {
        return null
      }

      return mapRelativePosition(
        mouseCoordinates.clientX,
        mouseCoordinates.clientY
      )
    },
    [mapRelativePosition, status]
  )

  const shouldRenderAnnotation = useCallback(
    (annotation: AnnotationDotFragment) => {
      if (annotation.currentSubject.__typename !== 'Artboard') {
        return false
      }

      const artboardUUID = annotation.currentSubject.uuid

      return !!artboards?.find(({ objectId }) => objectId === artboardUUID)
    },
    [artboards]
  )

  const [hideAnnotationsDots] = useHideAnnotationDots()

  return (
    <DraftCommentProvider>
      <AnnotationOverlayContext
        onMovingAnnotation={setLockCameraState}
        getElementAtPosition={getElementAtPosition}
        getPositionRelativeToOrigin={getPositionRelativeToOrigin}
        hidden={hideAnnotationsDots || hidden}
        shouldRenderAnnotation={shouldRenderAnnotation}
        {...otherProps}
      />
    </DraftCommentProvider>
  )
}
